import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { catchError, EMPTY, from, Observable, switchMap, take, throwError, timer } from 'rxjs';
import { AuthenticatorService } from './authenticatorService';
import { AmplifyService } from 'src/app/amplify/amplify.service';
import { TenantService } from './tenantService';
import { AuthSession } from 'aws-amplify/auth';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private readonly amplifyService = inject(AmplifyService);
  private readonly authenticationService = inject(AuthenticatorService);
  private readonly tenantService = inject(TenantService);

  private isRefreshing: boolean = false;

  private static readonly PUBLIC_PATTERNS: RegExp[] = [
    /\/legal\//i,
    /\/config(\?|$|\/)/i,
    /\/assets\//i,
    /\/terms(?!.*\/manage)/i,
    /\/forgot-password\//i,
    /\/mfa\/[^/]+\/recovery(\?|$|\/)/i,
  ];

  private static readonly PROTECTED_PATTERNS: RegExp[] = [/\/manage\//i];

  constructor() {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.isPublicEndpoint(request.url)) {
      return next.handle(request);
    }

    return this.checkMaintenanceMode(request, next);
  }

  private checkMaintenanceMode(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return this.tenantService.isMaintenanceMode().pipe(
      take(1),
      switchMap((maintenance: boolean) =>
        maintenance ? this.redirectToMaintenance() : this.authenticate(request, next)
      )
    );
  }

  private authenticate(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return from(this.amplifyService.isAuthenticated()).pipe(
      switchMap((isAuthenticated: boolean) => {
        if (!isAuthenticated) {
          this.authenticationService.logout();
          return throwError(() => new Error('User is not authenticated'));
        }
        return this.attachTokenAndExecute(request, next);
      })
    );
  }

  private attachTokenAndExecute(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return from(this.authenticationService.getToken()).pipe(
      switchMap((token: string | null) => {
        if (!token) {
          this.authenticationService.logout();
          return throwError(() => new Error('No token available'));
        }
        return next
          .handle(this.addAuthHeader(request, token))
          .pipe(catchError((error: HttpErrorResponse) => this.handleError(error, request, next)));
      })
    );
  }

  private handleError(
    error: HttpErrorResponse,
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (error.status === 401) {
      return this.handleUnauthorized(request, next);
    }

    if (error.status === 403) {
      return this.handleForbidden(error);
    }

    return throwError(() => error);
  }

  private handleForbidden(error: HttpErrorResponse): Observable<HttpEvent<any>> {
    const code = this.extractErrorCode(error);

    if (code === 'ACCESS_DENIED') {
      this.amplifyService.signOut().then(() => {
        globalThis.location.href = '/login?reason=deactivated';
      });
      return EMPTY;
    }

    return throwError(() => error);
  }

  private handleUnauthorized(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.isRefreshing) {
      return this.waitForRefreshAndRetry(request, next);
    }

    this.isRefreshing = true;
    return this.refreshSessionAndRetry(request, next);
  }

  private waitForRefreshAndRetry(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return timer(1000).pipe(
      switchMap(() => from(this.authenticationService.getToken())),
      switchMap((token: string | null) => {
        if (token) {
          return next.handle(this.addAuthHeader(request, token)).pipe(
            catchError(() => {
              this.authenticationService.logout();
              return throwError(() => new Error('Session expired. Please log in again.'));
            })
          );
        }
        this.authenticationService.logout();
        return throwError(() => new Error('Session expired'));
      })
    );
  }

  private refreshSessionAndRetry(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return from(this.amplifyService.fetchSessionForceRefresh()).pipe(
      catchError((refreshError) => {
        this.isRefreshing = false;
        console.error('Session refresh failed:', refreshError);
        this.authenticationService.logout();
        return throwError(() => new Error('Session expired. Please log in again.'));
      }),
      switchMap((session: AuthSession) => {
        this.isRefreshing = false;
        const newToken = session?.tokens?.idToken?.toString();

        if (!newToken) {
          console.warn('Token refresh returned no token');
          this.authenticationService.logout();
          return throwError(() => new Error('Session refresh failed'));
        }

        return next.handle(this.addAuthHeader(request, newToken));
      })
    );
  }

  private redirectToMaintenance(): Observable<never> {
    this.amplifyService.signOut().then(() => {
      globalThis.location.href = '/login?reason=maintenance';
    });
    return EMPTY;
  }

  private isPublicEndpoint(url: string): boolean {
    if (TokenInterceptor.PROTECTED_PATTERNS.some((p) => p.test(url))) {
      return false;
    }

    return TokenInterceptor.PUBLIC_PATTERNS.some((p) => p.test(url));
  }

  private addAuthHeader(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    });
  }

  private extractErrorCode(error: HttpErrorResponse): string | null {
    const body = error?.error;
    if (typeof body === 'object' && body?.code) {
      return body.code;
    }
    if (typeof body === 'string') {
      try {
        const parsed = JSON.parse(body);
        return parsed?.code ?? null;
      } catch {
        return null;
      }
    }
    return null;
  }

  private extractErrorMessage(error: HttpErrorResponse): string {
    const errorBody = error?.error;

    if (typeof errorBody === 'string') {
      return errorBody;
    }

    if (errorBody?.errors?.[0]) {
      return errorBody.errors[0];
    }

    if (errorBody?.message) {
      return errorBody.message;
    }

    if (error?.statusText && error.statusText !== 'Unknown Error') {
      return error.statusText;
    }

    return `Request failed with status ${error.status}`;
  }
}
